import { map, compose, extend, reject, isEmpty, some, omit, difference } from 'lodash/fp';
import { createSelector } from 'reselect';
import { getProp, getStringProp } from '@mews/fp';
import { composeProps, createPropsExtender, DecimalService } from '@mews/framework';
import { RouteName } from '@mews-commander/enums';
import { ResolveRoute } from '@mews/routing';
import { createScopedReducer, createOptionsSelector, sortByName } from '@mews-commander/utils';
import { getCurrencies } from '@mews-commander/core/legacy-globalization';
import { keyByCurrencyCode } from '@mews-commander/legacy-entities/src/ducks/exchangeRates';
import { getEnterpriseDefaultCurrencyCode } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { checkEnterprisePrivilege } from '@mews-commander/legacy-entities/src/selectors/session/privilegeChecks';
import { canCurrentProfileConfigureEnterprise } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import ConversionDirection from '../enums/ConversionDirection';
import {
    RATE_VALUE_CHANGED,
    RATE_DIRECTION_TOGGLED,
    RATE_STATUS_TOGGLED,
    SET_EXCHANGE_RATES,
    RATE_FIELD_DIRTY,
} from '../actions/types';
import moduleSelector from '../selector';

export const NAME = 'exchangeRates';

const initialState = {};

const { toNumber, parse, divide } = DecimalService;
export const divideDecimals = (a: $TSFixMe, b: $TSFixMe) => toNumber(divide(parse(a), parse(b)));

const baseToQuote = ConversionDirection.BaseToQuote;
const quoteToBase = ConversionDirection.QuoteToBase;

const remapConversionRate = (exchangeRate: $TSFixMe) => ({
    ...exchangeRate,
    conversionRate: exchangeRate.conversionRateToDefault,
});

const extendAndKeyByCurrencyCode = compose(
    keyByCurrencyCode,
    map(
        compose(
            extend({
                conversionDirection: baseToQuote,
                touched: false,
            }),
            omit(['conversionRateToDefault', 'conversionRateFromDefault']),
            remapConversionRate
        )
    )
);

export default createScopedReducer(initialState, {
    [SET_EXCHANGE_RATES]: (state: $TSFixMe, payload: $TSFixMe) => extendAndKeyByCurrencyCode(payload),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [RATE_VALUE_CHANGED]: (state, { currencyCode, newValue, errors }: $TSFixMe) => ({
        ...state,

        [currencyCode]: {
            ...state[currencyCode],
            errors,
            conversionRate: newValue,
        },
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [RATE_DIRECTION_TOGGLED]: (state, currencyCode) => ({
        ...state,

        [currencyCode]: {
            ...state[currencyCode],
            conversionRate: state[currencyCode].errors
                ? state[currencyCode].conversionRate
                : 1 / state[currencyCode].conversionRate,
            conversionDirection: state[currencyCode].conversionDirection === baseToQuote ? quoteToBase : baseToQuote,
        },
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [RATE_STATUS_TOGGLED]: (state, currencyCode) => ({
        ...state,

        [currencyCode]: {
            ...state[currencyCode],
            isAccepted: !state[currencyCode].isAccepted,
        },
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [RATE_FIELD_DIRTY]: (state, currencyCode) => ({
        ...state,

        [currencyCode]: {
            ...state[currencyCode],
            touched: true,
        },
    }),
});

export const getExchangeRates = createSelector(
    moduleSelector,
    getStringProp('scope'),
    (state, scope) => state.exchangeRates[scope] || {}
);

export const getAvailableNonExchangeCurrencies = createSelector(
    getExchangeRates,
    getCurrencies,
    (exchangeRates, currencies) => {
        const exchangeRateCodes = map('currencyCode')(exchangeRates);
        const allCurrencyCodes = map('code')(currencies);

        return compose(
            map((c) => ({ id: c, name: c })),
            difference(allCurrencyCodes)
        )(exchangeRateCodes);
    }
);

export const getAvailableNonExchangeCurrencyOptions = createOptionsSelector(getAvailableNonExchangeCurrencies, {
    sortFunction: sortByName,
});

export const getExchangeRatesWithoutDefault = createSelector(
    getExchangeRates,
    composeProps(getEnterpriseDefaultCurrencyCode, createPropsExtender('enterpriseId', getStringProp('scope'))),
    (exchangeRates, defaultCurrencyCode) => reject({ currencyCode: defaultCurrencyCode }, exchangeRates)
);

export const getExchangeRatesToUpdate = createSelector(getExchangeRatesWithoutDefault, (exchangeRatesWithoutDefault) =>
    map(
        // @ts-expect-error $TSFixMeError: Property 'conversionRate' does not exist on type '{ currencyCode: { toString: ...; charAt: ...; charCodeAt: ...; concat:...
        ({ currencyCode, conversionRate, conversionDirection, isAccepted }) => ({
            currencyCode,
            conversionRateToDefault:
                conversionDirection === baseToQuote
                    ? toNumber(parse(conversionRate))
                    : divideDecimals(1, conversionRate),
            conversionRateFromDefault:
                conversionDirection === baseToQuote
                    ? divideDecimals(1, conversionRate)
                    : toNumber(parse(conversionRate)),
            isAccepted,
        }),
        exchangeRatesWithoutDefault
    )
);

export const hasExchangeRatesErrors = createSelector(getExchangeRates, (exchangeRates) =>
    some((currency) => !isEmpty(currency.errors), exchangeRates)
);

export const getCreateExchangeRateUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringProp('enterpriseId'),
    (resolveRoute, enterpriseId) =>
        resolveRoute(RouteName.CurrencyAcceptanceCreate, { query: { OwnerId: enterpriseId } })
);

export const getExchangeRateIndexUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringProp('enterpriseId'),
    (resolveRoute, enterpriseId) =>
        resolveRoute(RouteName.CurrencyAcceptanceIndex, { query: { OwnerId: enterpriseId } })
);

export const checkExchangeRatesPrivilege = (state: $TSFixMe, { enterpriseId }: $TSFixMe) => {
    if (!enterpriseId) return false;

    return checkEnterprisePrivilege(state, { enterpriseId }) && canCurrentProfileConfigureEnterprise(state);
};
